import React, { useEffect, useState } from 'react';
import { adminApi, modelsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Loader2, Save, TriangleAlert, CheckCircle2, Plus, ArchiveRestore, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { PanelHeader } from './_shared';

const BLANK_NEW_MODEL = {
  id: '', label: '', provider: '', model: '', family: '',
  recommended_for: '', cost_tier: 'standard', cost_multiplier: '1.0', budget_tokens_chapter: '8000',
};

export default function ModelsPanel() {
  const [allModels, setAllModels] = useState([]);
  const [enabled, setEnabled] = useState([]);
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [costDrafts, setCostDrafts] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newModel, setNewModel] = useState(BLANK_NEW_MODEL);
  const [addBusy, setAddBusy] = useState(false);

  const reload = async () => {
    setLoading(true); setError('');
    try {
      const [cfg, list] = await Promise.all([adminApi.config(), modelsApi.adminList()]);
      const models = list.models || [];
      setAllModels(models);
      setEnabled(cfg.enabled_model_ids ?? models.map((model) => model.id));
      setHealth(list.catalog_health || null);
      setCostDrafts(Object.fromEntries(models.map((m) => [m.id, String(m.cost_multiplier ?? 1)])));
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail || 'Could not load model inventory.';
      setError(detail); toast.error(detail);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const toggle = (id) => setEnabled((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.saveConfig({ enabled_model_ids: enabled });
      toast.success('Enabled models updated.');
    } catch { toast.error('Save failed'); }
    finally { setBusy(false); }
  };

  const saveCost = async (id) => {
    const value = parseFloat(costDrafts[id]);
    if (Number.isNaN(value) || value <= 0) { toast.error('Cost multiplier must be a positive number.'); return; }
    try {
      await adminApi.updateModel(id, { cost_multiplier: value });
      toast.success('Costing updated.');
      reload();
    } catch (requestError) {
      toast.error(requestError?.response?.data?.detail || 'Could not update costing.');
    }
  };

  const toggleStatus = async (m) => {
    const nextStatus = m.status === 'deprecated' ? 'active' : 'deprecated';
    try {
      await adminApi.updateModel(m.id, { status: nextStatus });
      toast.success(nextStatus === 'deprecated' ? `${m.label} marked deprecated.` : `${m.label} reactivated.`);
      reload();
    } catch (requestError) {
      toast.error(requestError?.response?.data?.detail || 'Could not update status.');
    }
  };

  const markReviewed = async () => {
    setReviewBusy(true);
    try {
      const r = await adminApi.markModelsReviewed();
      setHealth(r.catalog_health);
      toast.success('Catalog marked reviewed for this week.');
    } catch { toast.error('Could not mark reviewed.'); }
    finally { setReviewBusy(false); }
  };

  const submitNewModel = async (e) => {
    e.preventDefault();
    setAddBusy(true);
    try {
      await adminApi.addModel({
        ...newModel,
        cost_multiplier: parseFloat(newModel.cost_multiplier) || 1,
        budget_tokens_chapter: parseInt(newModel.budget_tokens_chapter, 10) || 8000,
      });
      toast.success('Model added to the catalog.');
      setNewModel(BLANK_NEW_MODEL);
      setShowAddForm(false);
      reload();
    } catch (requestError) {
      toast.error(requestError?.response?.data?.detail || 'Could not add model.');
    } finally { setAddBusy(false); }
  };

  return (
    <section>
      <PanelHeader title="AI Models" subtitle="Toggle which models customers can pick, edit per-model costing and keep the catalog current. Disabled models stay hidden in the project & settings dropdowns." onRefresh={reload} />

      {health && (
        <div
          className={
            'mt-4 flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm ' +
            (health.stale ? 'border-amber-400/50 bg-amber-50 text-amber-900' : 'border-emerald-400/40 bg-emerald-50 text-emerald-900')
          }
          data-testid="admin-models-catalog-health"
        >
          <div className="flex items-center gap-2">
            {health.stale ? <TriangleAlert className="h-4 w-4 shrink-0" /> : <CheckCircle2 className="h-4 w-4 shrink-0" />}
            <span>
              {health.last_reviewed_at
                ? `Catalog last reviewed ${health.days_since_review} day${health.days_since_review === 1 ? '' : 's'} ago.`
                : 'Catalog has never been marked reviewed.'}
              {health.stale && ' Providers release & deprecate models often — give it a quick check.'}
            </span>
          </div>
          <Button size="sm" variant="outline" onClick={markReviewed} disabled={reviewBusy} data-testid="admin-models-mark-reviewed">
            {reviewBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Mark reviewed'}
          </Button>
        </div>
      )}

      {loading && <div className="mt-4 flex items-center gap-2 rounded-md border border-border bg-card p-4 text-sm text-muted-foreground" data-testid="admin-models-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading model inventory…</div>}
      {error && <div className="mt-4 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive" role="alert" data-testid="admin-models-error"><TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" /><span>{error}</span></div>}

      <div className="mt-4 rounded-md border border-border bg-card divide-y divide-border">
        {allModels.map((m) => {
          const on = enabled.includes(m.id);
          const deprecated = m.status === 'deprecated';
          return (
            <div key={m.id} className={'flex flex-wrap items-center gap-3 px-3 py-2.5' + (deprecated ? ' opacity-60' : '')} data-testid={`admin-model-row-${m.id}`}>
              <div className="min-w-[160px] flex-1">
                <div className="flex items-center gap-2 text-[13px] font-medium">
                  {m.label}
                  <span className="font-mono-stat text-[10px] text-muted-foreground">{m.provider}</span>
                  {m.is_custom && <span className="rounded-full bg-muted px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-muted-foreground">Custom</span>}
                  {deprecated && <span className="rounded-full bg-destructive/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-destructive">Deprecated</span>}
                </div>
                <div className="text-[11.5px] text-muted-foreground">{m.recommended_for}</div>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">cost ×</span>
                <Input
                  value={costDrafts[m.id] ?? ''}
                  onChange={(e) => setCostDrafts((d) => ({ ...d, [m.id]: e.target.value }))}
                  onBlur={() => { if (costDrafts[m.id] !== String(m.cost_multiplier)) saveCost(m.id); }}
                  className="h-7 w-16 font-mono-stat text-[11.5px]"
                  data-testid={`admin-model-cost-input-${m.id}`}
                />
              </div>
              <Button
                size="icon" variant="ghost" className="h-7 w-7"
                title={deprecated ? 'Reactivate model' : 'Mark deprecated'}
                onClick={() => toggleStatus(m)}
                data-testid={`admin-model-status-toggle-${m.id}`}
              >
                {deprecated ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
              </Button>
              <Switch checked={on} onCheckedChange={() => toggle(m.id)} data-testid={`admin-model-toggle-${m.id}`} />
            </div>
          );
        })}
        {!loading && !error && !allModels.length && <div className="p-6 text-center text-sm text-muted-foreground" data-testid="admin-models-empty">No models are registered.</div>}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" onClick={save} disabled={busy} className="gap-1.5" data-testid="admin-models-save"><Save className="h-3.5 w-3.5" /> Save enabled models</Button>
        <Button size="sm" variant="outline" onClick={() => setShowAddForm((v) => !v)} className="gap-1.5" data-testid="admin-models-add-toggle"><Plus className="h-3.5 w-3.5" /> Add model</Button>
      </div>

      {showAddForm && (
        <form onSubmit={submitNewModel} className="mt-3 grid grid-cols-2 gap-2 rounded-md border border-border bg-card p-3 sm:grid-cols-4" data-testid="admin-models-add-form">
          <Input placeholder="id (e.g. claude-haiku-5)" value={newModel.id} onChange={(e) => setNewModel((v) => ({ ...v, id: e.target.value }))} required data-testid="admin-model-new-id" />
          <Input placeholder="Label" value={newModel.label} onChange={(e) => setNewModel((v) => ({ ...v, label: e.target.value }))} required data-testid="admin-model-new-label" />
          <Input placeholder="provider (anthropic/openai/gemini)" value={newModel.provider} onChange={(e) => setNewModel((v) => ({ ...v, provider: e.target.value }))} required data-testid="admin-model-new-provider" />
          <Input placeholder="model slug" value={newModel.model} onChange={(e) => setNewModel((v) => ({ ...v, model: e.target.value }))} required data-testid="admin-model-new-model" />
          <Input placeholder="Recommended for" value={newModel.recommended_for} onChange={(e) => setNewModel((v) => ({ ...v, recommended_for: e.target.value }))} className="col-span-2" data-testid="admin-model-new-recommended" />
          <Input placeholder="Cost multiplier" value={newModel.cost_multiplier} onChange={(e) => setNewModel((v) => ({ ...v, cost_multiplier: e.target.value }))} data-testid="admin-model-new-cost" />
          <Button type="submit" size="sm" disabled={addBusy} className="gap-1.5" data-testid="admin-model-new-submit">
            {addBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
          </Button>
        </form>
      )}
    </section>
  );
}
